import {
  CallHandler,
  ExecutionContext,
  Injectable,
  NestInterceptor,
} from '@nestjs/common';
import { randomUUID } from 'crypto';
import { Request, Response } from 'express';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { AuthenticatedRequest } from '../auth/auth.guard';
import { StoreContextService } from '../tenancy/store-context.service';
import { AuditService } from './audit.service';
import { AuditContextService, AuditContextState, uuidv7 } from './audit-context.service';
import { AuditBatchService } from './audit-batch.service';
import {
  capPayload,
  decideSampling,
  isExcludedPath,
  pickHeaders,
  routePattern,
} from './audit-sampling';

interface RequestWithAudit extends AuthenticatedRequest {
  auditCorrelationId?: string;
  auditBatchId?: string;
}

@Injectable()
export class AuditInterceptor implements NestInterceptor {
  constructor(
    private readonly auditService: AuditService,
    private readonly auditContext: AuditContextService,
    private readonly batches: AuditBatchService,
    private readonly storeContext?: StoreContextService
  ) {}

  intercept(context: ExecutionContext, next: CallHandler): Observable<unknown> {
    if (context.getType() !== 'http') return next.handle();

    const http = context.switchToHttp();
    const request = http.getRequest<RequestWithAudit>();
    const response = http.getResponse<Response>();
    const path = request.originalUrl ?? request.url ?? '';

    // Cheapest possible check, first. Anything past this point costs work, and
    // the whole point of the filter is to not do that work.
    if (isExcludedPath(path)) return next.handle();

    const startedAt = Date.now();
    const requestedOn = new Date(startedAt);
    const correlationId = this.resolveCorrelationId(request);

    // Server generated, always. Never the client's x-correlation-id header:
    // that is attacker-controlled, and a grouping key an untrusted caller can
    // set would let one merchant merge their changes into another's batch, or
    // split their own apart. The header stays useful as a trace id; it is just
    // not allowed to be the thing that groups an audit record.
    const batchId = uuidv7();

    request.auditCorrelationId = correlationId;
    request.auditBatchId = batchId;

    const state: AuditContextState = {
      batchId,
      correlationId,
      actorId: request.user?.id,
      actorRole: request.user?.role,
      // AuthUser carries no display name — email is the readable identifier the
      // token actually has, and it is what the admin's actor column shows.
      actorName: request.user?.email,
      ipAddress: request.ip,
      userAgent: this.header(request, 'user-agent'),
      storeId: this.storeContext?.storeId(),
      organizationId: this.storeContext?.organizationId(),
      route: path,
      method: request.method,
      source: 'api',
    };

    // The SUBSCRIBE has to happen inside run(), not just the call to
    // next.handle().
    //
    // next.handle() returns a lazy observable — the route handler does not
    // execute until something subscribes, and that subscription is performed by
    // the framework after this method has already returned. Wrapping only the
    // handle() call therefore left the AsyncLocalStorage context inactive for
    // the entire request: actorPayload() came back undefined, so the
    // transaction interceptor never published an actor and every trigger row
    // landed as `system` with a null batchId. Subscribing inside run() is what
    // makes the context cover the handler and everything it awaits.
    return new Observable((subscriber) =>
      this.auditContext.run(state, () =>
        next
          .handle()
          .pipe(
            tap((payload) => {
              this.record(request, path, response.statusCode, requestedOn, startedAt, payload, undefined);
              this.closeBatch(state, response.statusCode);
            }),
            catchError((error: unknown) => {
              const statusCode = this.statusCode(error);
              this.record(request, path, statusCode, requestedOn, startedAt, undefined, error);
              this.closeBatch(state, statusCode);
              return throwError(() => error);
            })
          )
          .subscribe(subscriber)
      )
    );
  }

  private record(
    request: RequestWithAudit,
    path: string,
    statusCode: number,
    requestedOn: Date,
    startedAt: number,
    payload: unknown,
    error: unknown
  ): void {
    const decision = decideSampling(request.method, path, statusCode);
    if (!decision.record) return;

    const requestBody = decision.captureRequestBody
      ? capPayload(this.auditService.maskPayload(request.body))
      : { value: undefined, truncated: false };

    // Only ever for a 5xx. Storing the response of every successful request was
    // the bulk of this table's size and nearly all of its CPU cost, since the
    // masking walk scales with payload size.
    const responseBody = decision.captureResponseBody
      ? capPayload(this.auditService.maskPayload(payload))
      : { value: undefined, truncated: false };

    this.auditService.logInterface({
      correlationId: request.auditCorrelationId ?? randomUUID(),
      batchId: request.auditBatchId,
      method: request.method,
      path,
      routePattern: routePattern(path),
      direction: 'inbound',
      statusCode,
      requestHeaders: pickHeaders(request.headers as Record<string, unknown>),
      requestPayload: requestBody.value,
      responsePayload: responseBody.value,
      errorPayload: error ? this.errorPayload(error) : undefined,
      truncated: requestBody.truncated || responseBody.truncated,
      sampleRate: decision.sampleRate,
      userId: request.user?.id,
      ipAddress: request.ip,
      userAgent: this.header(request, 'user-agent'),
      durationMs: Date.now() - startedAt,
      requestedOn,
      respondedOn: new Date(),
    });
  }

  /**
   * Writes the batch header, but only for requests that actually changed
   * something. A GET opens a context (so any incidental write is still
   * attributed) yet must not leave an empty row in the admin's change list.
   */
  private closeBatch(state: AuditContextState, statusCode: number): void {
    if (state.method === 'GET' || statusCode >= 400) return;

    this.batches.close(state);
  }

  private header(request: Request, name: string): string | undefined {
    const value = request.headers?.[name];
    return Array.isArray(value) ? value[0] : value;
  }

  private resolveCorrelationId(request: Request): string {
    const header = request.headers['x-correlation-id'];

    if (Array.isArray(header)) {
      return header[0] ?? randomUUID();
    }

    return header || randomUUID();
  }

  private statusCode(error: unknown): number {
    if (typeof error === 'object' && error && 'status' in error) {
      const status = (error as { status?: unknown }).status;
      return typeof status === 'number' ? status : 500;
    }

    return 500;
  }

  private errorPayload(error: unknown): object {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
      };
    }

    return { message: String(error) };
  }
}
